import { Code, ConnectError } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import { format, startOfDay, subDays } from "date-fns";
import { ja } from "date-fns/locale";
import { useState } from "react";
import toast from "react-hot-toast";
import Close from "~icons/material-symbols/close.svg";
import Download from "~icons/material-symbols/download";
import { css } from "../../../../../styled-system/css";
import { DateTimeRangePicker } from "../../../../components/date-time-range-picker";
import { LoadingOverlay } from "../../../../components/loading-overlay";
import {
  useGetHokkaidoR8HouseholderPaperVouchersByPeriod,
  useGetHokkaidoR8HouseholderPostalReturnsByPeriod,
} from "../../../../libs/admin/hooks";
import {
  buildPeriodFilename,
  downloadBlobFile,
  downloadCsvFile,
} from "../../../../libs/utils/csv";
import { button } from "../../../../styles/buttonStyles";
import { contentWrapper } from "../../../../styles/contentWrapper";
import { input, inputButton, inputWithButtonContainer } from "../../../../styles/inputStyles";
import {
  pageTitle,
  sectionTitle,
  sectionWrapper,
  titleWrapper,
} from "../../../../styles/sectionStyles";
import { buildPaperVoucherCsv, buildPostalReturnZip } from "./-csv/export-csv";

export const Route = createFileRoute("/admin/hokkaido/exports/")({
  component: RouteComponent,
});

const errorMessage = (error: unknown): string => {
  if (error instanceof ConnectError) {
    if (error.code === Code.PermissionDenied) {
      return "権限がありません。組織を確認してください。";
    }
    if (error.code === Code.InvalidArgument) {
      return "件数が多すぎるか期間が不正です。期間を短くして再度お試しください。";
    }
    return error.message;
  }
  return "通信に失敗しました。時間を置いて再度お試しください。";
};

function RouteComponent() {
  const paperVoucherMutation = useGetHokkaidoR8HouseholderPaperVouchersByPeriod();
  const postalReturnMutation = useGetHokkaidoR8HouseholderPostalReturnsByPeriod();

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>CSV出力</h1>
      </div>
      <div className={css({ display: "flex", flexDirection: "column", gap: "24px" })}>
        <ExportSection
          title="ギフト券交付 CSV出力"
          description="指定期間内にギフト券交付済みとして登録された世帯主を出力します。"
          isPending={paperVoucherMutation.isPending}
          run={async (start, end) => {
            const rows = await paperVoucherMutation.mutateAsync({ start, end });
            const csv = buildPaperVoucherCsv(rows);
            return {
              count: rows.length,
              download: () =>
                downloadCsvFile(
                  buildPeriodFilename("paper_vouchers", start, end, "csv"),
                  csv,
                ),
            };
          }}
        />
        <ExportSection
          title="ハガキ返戻 CSV出力"
          description="指定期間内に返戻済みとして登録された世帯主を市区町村ごとのCSVに分けてzipで出力します（同一シリアルナンバーは期間内で最新の返戻1件）。"
          isPending={postalReturnMutation.isPending}
          run={async (start, end) => {
            const rows = await postalReturnMutation.mutateAsync({ start, end });
            const zip = await buildPostalReturnZip(rows, start, end);
            return {
              count: rows.length,
              download: () =>
                downloadBlobFile(
                  buildPeriodFilename("postal_returns", start, end, "zip"),
                  zip,
                ),
            };
          }}
        />
      </div>
    </div>
  );
}

function ExportSection(props: {
  title: string;
  description: string;
  isPending: boolean;
  run: (
    start: Date,
    end: Date,
  ) => Promise<{ count: number; download: () => void }>;
}) {
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const periodValid = start !== null && end !== null && start <= end;
  const canExport = periodValid && !props.isPending;

  const handleExport = async () => {
    if (!start || !end) return;
    if (
      !window.confirm(
        "このCSVの情報は、住民の方からの問い合わせへの回答には利用できません。問い合わせ回答以外の業務での利用で間違いありませんか？",
      )
    ) {
      return;
    }
    setError(null);
    try {
      const { count, download } = await props.run(start, end);
      if (count === 0) {
        toast("該当するデータがありません", { icon: "ℹ️" });
        return;
      }
      download();
      toast.success(`${count} 件を出力しました`);
    } catch (err) {
      console.error("Failed to export CSV:", err);
      setError(errorMessage(err));
    }
  };

  return (
    <div className={sectionWrapper()}>
      <div className={sectionTitle()}>{props.title}</div>
      <div className={css({ display: "flex", flexDirection: "column", gap: "12px" })}>
        <p className={css({ fontSize: "14px", color: "text.secondary" })}>
          {props.description}
        </p>
        <div className={css({ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "end" })}>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              width: "360px",
              position: "relative",
            })}
          >
            <div className={css({ fontSize: "12px" })}>期間</div>
            <div className={inputWithButtonContainer()}>
              <button
                type="button"
                className={css(input.raw(), {
                  display: "flex",
                  alignItems: "center",
                  textAlign: "left",
                  cursor: "pointer",
                  pr: "40px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: start ? "text.primary" : "text.placeholder",
                })}
                onClick={() => setIsDatePickerOpen((prev) => !prev)}
              >
                {start && end
                  ? `${format(start, "yyyy/MM/dd HH:mm", { locale: ja })} 〜 ${format(end, "yyyy/MM/dd HH:mm", { locale: ja })}`
                  : "指定なし"}
              </button>
              {start && (
                <button
                  type="button"
                  className={inputButton({ semantic: "danger" })}
                  onClick={() => {
                    setStart(null);
                    setEnd(null);
                  }}
                >
                  <Close />
                </button>
              )}
            </div>
            {isDatePickerOpen && (
              <>
                <div
                  className={css({
                    position: "fixed",
                    inset: 0,
                    zIndex: 25,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                  })}
                  onClick={() => setIsDatePickerOpen(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setIsDatePickerOpen(false);
                  }}
                />
                <div
                  className={css({
                    position: "absolute",
                    top: "62px",
                    left: 0,
                    zIndex: 30,
                    bg: "white",
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "border.secondary",
                    shadow: "md",
                    p: "12px",
                    maxWidth: "fit-content",
                    overflowX: "auto",
                  })}
                >
                  <DateTimeRangePicker
                    limit={start ?? startOfDay(subDays(new Date(), 7))}
                    offset={end ?? new Date()}
                    onSubmit={({ limit, offset }) => {
                      setStart(limit);
                      setEnd(offset);
                      setIsDatePickerOpen(false);
                    }}
                    onCancel={() => setIsDatePickerOpen(false)}
                  />
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            className={button({ color: "primary" })}
            onClick={handleExport}
            disabled={!canExport}
          >
            <Download width="20" height="20" />
            CSV出力
            {props.isPending && <LoadingOverlay size="sm" />}
          </button>
        </div>
        {start && end && start > end && (
          <div className={css({ fontSize: "13px", color: "text.accentError" })}>
            終了日時は開始日時以降を指定してください。
          </div>
        )}
        {error && (
          <div className={css({ fontSize: "14px", color: "text.accentError" })}>{error}</div>
        )}
      </div>
    </div>
  );
}
